// frontend/src/App.js
import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// Auth Provider - Removed, now using Zustand store directly
// import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ConversationProvider } from "./contexts/ConversationContext";
import { ChakraProvider } from "@chakra-ui/react";
import { useAuth } from "./hooks/useAuth";
import theme from "./theme";
import { colorModeManager } from "./utils/colorModeManager";
import ScrollToTop from "./components/ScrollToTop";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { setupAuthInterceptor, checkTokenExpiration } from './api/interceptors';
import { useTokenAutoRefresh } from './hooks/useTokenAutoRefresh';

// Import toast styles
import "./styles/toast.css";
import "./styles/chakra-toast-fixes.css";

// Analytics Components
import PageTracker from "./components/analytics/PageTracker";
import { AnalyticsProvider } from "./contexts/AnalyticsContext";

// Layout Components
import Layout from "./components/layout/Layout";

// Pages
import LoginPage from "./pages/Auth/LoginPage";
import SignupPage from "./pages/Auth/SignupPage";
import EmailVerificationPageWrapper from "./pages/Auth/EmailVerificationPageWrapper";
import ForgotPasswordPage from "./pages/Auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/Auth/ResetPasswordPage";
import LoadingPage from "./pages/Landing/LoadingPage"; // Import directly, not lazy!
import ChatPage from "./pages/Chat/ChatPage"; // Import directly to avoid Suspense issues
// import ChatPage from "./pages/Chat/ChatPage.debug"; // TEMPORARY: Testing minimal version to isolate Error #185
import ChatEntry from "./pages/Chat/ChatEntry";
import ProtectedRoute from "./components/ProtectedRoute"; // Import directly to avoid Suspense issues in guarded routes

// Components
import SuspenseErrorBoundary from "./components/SuspenseErrorBoundary";
import ChatErrorBoundary from "./components/ChatErrorBoundary";
import lazyWithRetry from "./utils/lazyWithRetry";

// Lazy loaded pages
const HomePage = lazyWithRetry(() => import("./pages/Landing/HomePage"), { componentName: 'HomePage' });
const DashboardHome = lazyWithRetry(() => import("./pages/Dashboard/HomePage"), { componentName: 'DashboardHome' });
const CoursesPage = lazyWithRetry(() => import("./pages/Dashboard/CoursesPage"), { componentName: 'CoursesPage' });
const ProfilePage = lazyWithRetry(() => import("./pages/Dashboard/ProfilePage"), { componentName: 'ProfilePage' });
const BillingPage = lazyWithRetry(() => import("./pages/Dashboard/BillingPage"), { componentName: 'BillingPage' });
// ChatPage is imported directly above to avoid Suspense issues
const MyDocumentsPage = lazyWithRetry(() => import("./pages/Dashboard/MyDocumentsPage"), { componentName: 'MyDocumentsPage' });
const DirectStreamingPage = lazyWithRetry(
  () => import("./pages/Dashboard/DirectStreamingPage"),
  { componentName: 'DirectStreamingPage' }
);
// PageLoader removed - LoadingPage is imported directly above
// const ChatIntegrationTest = lazy(() => import("./tests/ChatIntegrationTest"));
// const ChatE2ETest = lazy(() => import("./tests/ChatE2ETest"));
// const AssistantUIDebug = lazy(() => import("./tests/AssistantUIDebug"));
const AuthDebugPage = lazyWithRetry(() => import("./pages/Dashboard/AuthDebugPage"), { componentName: 'AuthDebugPage' });
const BypassTestPage = lazyWithRetry(() => import("./pages/Dashboard/BypassTestPage"), { componentName: 'BypassTestPage' });
const AboutPage = lazyWithRetry(() => import("./pages/Landing/AboutPage"), { componentName: 'AboutPage' });
const ContactPage = lazyWithRetry(() => import("./pages/Landing/ContactPage"), { componentName: 'ContactPage' });
const PricingPage = lazyWithRetry(() => import("./pages/Landing/PricingPage"), { componentName: 'PricingPage' });
const TermsOfServicePage = lazyWithRetry(() => import("./pages/Landing/TermsOfServicePage"), { componentName: 'TermsOfServicePage' });
const PrivacyPolicyPage = lazyWithRetry(() => import("./pages/Landing/PrivacyPolicyPage"), { componentName: 'PrivacyPolicyPage' });
// Explicitly import the Diagnostics directory index to avoid colliding with pages/Diagnostics.tsx
const DiagnosticsPage = lazyWithRetry(() => import("./pages/Diagnostics/index"), { componentName: 'DiagnosticsPage' });

// Onboarding Components - Import directly to avoid critical-path issues
import OnboardingContainer from "./components/onboarding/OnboardingContainer";

// Add the new debug auth component
const DebugAuthPage = lazyWithRetry(() => import("./pages/Diagnostics/DebugAuth"), { componentName: 'DebugAuthPage' });

// Test components
// Import guard routes directly to avoid critical-path chunk load flakiness
import OnboardingProtectedRoute from "./components/OnboardingProtectedRoute";
import SuperuserRoute from "./components/SuperuserRoute";
const NotFound = lazyWithRetry(() => import("./pages/NotFound"), { componentName: 'NotFound' });
const StreamingTestPage = lazyWithRetry(() => import("./pages/StreamingTestPage"), { componentName: 'StreamingTestPage' });
const ChatDocChipTest = lazyWithRetry(() => import('./pages/Diagnostics/ChatDocChipTest'), { componentName: 'ChatDocChipTest' });
const SkillGraphTest = lazyWithRetry(() => import('./pages/SkillGraphTest'), { componentName: 'SkillGraphTest' });
const SplitChatTestPage = lazyWithRetry(() => import('./pages/Chat/SplitChatTestPage'), { componentName: 'SplitChatTestPage' });

// Import auth diagnostics in development
if (process.env.NODE_ENV === 'development') {
  import('./utils/authDiagnostics');
}

// Import version checking for production
if (process.env.NODE_ENV === 'production') {
  import('./utils/versionCheck').then(({ startVersionCheck }) => {
    startVersionCheck();
  });
}

// Auth Route Component (redirects to dashboard if already authenticated)
const AuthRoute = ({ children }) => {
  const { isAuthenticated, isLoading, isRegistering, hasHydrated } = useAuth();
  const location = useLocation();

  console.log("🔥 AUTHROUTE: Current state", { isLoading, isRegistering, hasHydrated, isAuthenticated: isAuthenticated() });

  // Wait for auth store to hydrate from localStorage
  if (!hasHydrated) {
    console.log("🔥 AUTHROUTE: Waiting for hydration...");
    return <LoadingPage />;
  }

  // Don't show PageLoader during registration - let SignupForm handle its own loading state
  if (isLoading && !isRegistering) {
    console.log("🔥 AUTHROUTE: Showing PageLoader due to isLoading=true (not registering)");
    return <LoadingPage />;
  }

  // Don't redirect if user is in the middle of registering
  // This prevents the flash to dashboard/chat before email verification
  if (isAuthenticated() && !isRegistering) {
    // Redirect to the page the user was trying to access (saved in location state)
    // or default to dashboard
    const from = location.state?.from?.pathname || "/dashboard";
    return <Navigate to={from} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        {/* Expose skill-graph test in all envs for online testing */}
        <Route
          path="/skill-graph-test"
          element={
            <Layout loadFooter={false}>
              <SkillGraphTest />
            </Layout>
          }
        />
        <Route
          path="/about"
          element={
            <Layout>
              <AboutPage />
            </Layout>
          }
        />
        <Route
          path="/contact"
          element={
            <Layout>
              <ContactPage />
            </Layout>
          }
        />
        <Route
          path="/pricing"
          element={
            <Layout>
              <PricingPage />
            </Layout>
          }
        />
        <Route
          path="/terms"
          element={
            <Layout>
              <TermsOfServicePage />
            </Layout>
          }
        />
        <Route
          path="/privacy"
          element={
            <Layout>
              <PrivacyPolicyPage />
            </Layout>
          }
        />

        {/* Auth routes (redirect to dashboard if already logged in) */}
        <Route
          path="/login"
          element={
            <AuthRoute>
              <Layout hideNavForAuth loadFooter={false}>
                <LoginPage />
              </Layout>
            </AuthRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <AuthRoute>
              <Layout hideNavForAuth loadFooter={false}>
                <SignupPage />
              </Layout>
            </AuthRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <Layout hideNavForAuth loadFooter={false}>
              <ForgotPasswordPage />
            </Layout>
          }
        />
        <Route
          path="/reset-password"
          element={
            <Layout hideNavForAuth loadFooter={false}>
              <ResetPasswordPage />
            </Layout>
          }
        />
        <Route
          path="/verify-email"
          element={
            <Layout hideNavForAuth loadFooter={false}>
              <EmailVerificationPageWrapper />
            </Layout>
          }
        />
        {/* Optional variant used by ProtectedRoute; map to same wrapper */}
        <Route
          path="/verify-email-optional"
          element={
            <Layout hideNavForAuth loadFooter={false}>
              <EmailVerificationPageWrapper />
            </Layout>
          }
        />

        {/* Dashboard route with footer enabled */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout loadFooter={true}>
                <DashboardHome />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Protected routes with persistent Layout (footer disabled) */}
        <Route
          element={
            <ProtectedRoute>
              <Layout loadFooter={false} />
            </ProtectedRoute>
          }
        >
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/documents" element={<MyDocumentsPage />} />
        </Route>

        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              {/* Empty chat view: no conversation until first message; keeps /chat URL */}
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:conversationId"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        
        {/* Test routes - only in development */}
        {process.env.NODE_ENV === 'development' && (
          <>
            {/* Public Chat test maps to actual ChatPage */}
            <Route path="/chat-test" element={<ChatPage />} />
            <Route path="/chat-test/:conversationId" element={<ChatPage />} />
            {/* Mock Split Chat UI test lives under a new route */}
            <Route path="/split-chat-test" element={<SplitChatTestPage />} />
            <Route path="/split-chat-test/:conversationId" element={<SplitChatTestPage />} />
            {/* Skill Graph test */}
            <Route
              path="/skill-graph-test"
              element={
                <Layout loadFooter={false}>
                  <SkillGraphTest />
                </Layout>
              }
            />
            {/* Chat doc chip test */}
            <Route path="/chat-doc-test" element={<ChatDocChipTest />} />
            {/* Public Dashboard for testing */}
            <Route
              path="/dashboard-test"
              element={
                <Layout loadFooter={true}>
                  <DashboardHome />
                </Layout>
              }
            />
            {/* Public Courses for testing */}
            <Route
              path="/courses-test"
              element={
                <Layout loadFooter={false}>
                  <CoursesPage />
                </Layout>
              }
            />
            {/* Public Billing for testing */}
            <Route
              path="/billing-test"
              element={
                <Layout loadFooter={false}>
                  <BillingPage />
                </Layout>
              }
            />
            {/* Public Documents for testing (bypasses auth) */}
            <Route
              path="/documents-test"
              element={
                <Layout loadFooter={false}>
                  <MyDocumentsPage />
                </Layout>
              }
            />
            {/* Onboarding test route - bypasses auth */}
            <Route
              path="/onboarding-test"
              element={<OnboardingContainer />}
            />
            {/* Profile test route - bypasses auth */}
            <Route
              path="/profile-test"
              element={
                <Layout loadFooter={false}>
                  <ProfilePage />
                </Layout>
              }
            />
          </>
        )}
        
        {/* Test routes disabled to avoid type drift issues during development */}
        <Route
          path="/direct-stream"
          element={
            <ProtectedRoute>
              <Layout>
                <DirectStreamingPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/diagnostics"
          element={
            <SuperuserRoute>
              <Layout>
                <DiagnosticsPage />
              </Layout>
            </SuperuserRoute>
          }
        />
        <Route
          path="/auth-debug"
          element={
            <ProtectedRoute>
              <Layout>
                <AuthDebugPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bypass-test"
          element={
            <ProtectedRoute>
              <Layout>
                <BypassTestPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/streaming-test"
          element={
            <ProtectedRoute>
              <StreamingTestPage />
            </ProtectedRoute>
          }
        />
        {/* Add the new Debug Auth page route */}
        <Route
          path="/debug-auth"
          element={
            <ProtectedRoute>
              <Layout>
                <DebugAuthPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* Onboarding Flow Route */}
        <Route
          path="/onboarding"
          element={
            <OnboardingProtectedRoute>
              <OnboardingContainer />
            </OnboardingProtectedRoute>
          }
        />
        
        {/* Legacy complete-profile route for backwards compatibility */}
        <Route
          path="/complete-profile"
          element={<Navigate to="/onboarding" replace />}
        />
        {/* 404 Not Found route (direct) */}
        <Route
          path="/404"
          element={
            <Layout>
              <NotFound />
            </Layout>
          }
        />
        {/* Fallback route: render inline without URL redirect to avoid flicker */}
        <Route
          path="*"
          element={
            <Layout>
              <NotFound />
            </Layout>
          }
        />
      </Routes>
  );
}

// Wrapper component that has access to useLocation
function AppWithErrorBoundary() {
  const location = useLocation();
  // Wrap route rendering in Suspense so lazy() routes show a loader instead of throwing
  const content = (
    <>
      <ScrollToTop />
      <PageTracker />
      <Suspense fallback={<LoadingPage />}>
        <AppRoutes />
      </Suspense>
    </>
  );

  // Wrap chat routes with ChatErrorBoundary for specialized error handling
  if (location.pathname.startsWith('/chat')) {
    return (
      <ChatErrorBoundary>
        {content}
      </ChatErrorBoundary>
    );
  }

  return (
    <SuspenseErrorBoundary resetKey={location.pathname}>
      {content}
    </SuspenseErrorBoundary>
  );
}

// Initialize auth interceptors and proactive expiry checks inside providers
function AuthInit() {
  const { token, logout, hasHydrated } = useAuth();
  useEffect(() => { setupAuthInterceptor(logout); }, [logout]);
  useEffect(() => { if (hasHydrated) { checkTokenExpiration(token ?? null); } }, [hasHydrated, token]);
  // Enable sliding-session refresh for active users
  useTokenAutoRefresh();
  return null;
}

function App() {
  const queryClient = React.useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        // Don't use suspense by default - we'll opt-in where needed (removed unsupported option)
        // Prevent excessive refetching
        staleTime: 1000 * 60 * 1, // 1 minute
        gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
        // Better retry configuration
        retry: (failureCount, error: any) => {
          // Don't retry on 4xx errors except 429 (rate limit)
          if (error?.status >= 400 && error?.status < 500 && error?.status !== 429) {
            return false;
          }
          // Retry up to 3 times for other errors
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Prevent refetch on window focus during development
        refetchOnWindowFocus: process.env.NODE_ENV === 'production',
        // Don't refetch on reconnect automatically
        refetchOnReconnect: 'always',
      },
      mutations: {
        // Mutations should not use suspense (removed unsupported option)
        retry: 1,
        retryDelay: 1000,
      },
    },
  }), []);

  // AuthInit handles interceptor wiring and expiry checks within providers
  return (
    <Router>
      <ChakraProvider theme={theme} colorModeManager={colorModeManager}>
        <ToastProvider>
          <ThemeProvider>
            <QueryClientProvider client={queryClient}>
              <AnalyticsProvider enablePageTracking={true} enableSessionRecording={true}>
                <AuthInit />
                <SubscriptionProvider>
                  <ConversationProvider>
                    <AppWithErrorBoundary />
                  </ConversationProvider>
                </SubscriptionProvider>
              </AnalyticsProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </ToastProvider>
      </ChakraProvider>
    </Router>
  );
}

export default App;
